import {
	OrderQuoteOfferStatus,
	OrderSampleChoice,
	useGenerateOfferEconomicQuotePdfMutation,
	useGetClientOrderQuoteOfferBatchSummaryLazyQuery,
	useSaveOfferBatchSelectionMutation,
} from '@codezo/graphql';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useQuoteCurrency } from '../context';
import type { Batch, ClientQuoteItem, Offer } from '../components/offer-types';
import type { FilePreviewItem } from '../../../../components/ui/file-preview';

/**
 * Everything the client is deciding on one offer batch, and what it costs.
 *
 * The client assembles their own order out of the grid the offer authors: a
 * quantity tier per product, a shipping method per tier, a sample choice per
 * product, which products are in at all — or one combination plan that pins
 * several of those at once. Five interlocking answers, each of which invalidates
 * some of the others, plus a server-priced total that has to follow all five.
 *
 * That state lives HERE rather than in a card because there is more than one
 * card. The v1 card and the v2 one (`v2/ClientOfferBatchV2`) draw the same
 * decisions in different shapes; forking the rules that connect them would mean
 * the two layouts could disagree about what a combination costs, which is the
 * one thing a quote cannot do. It is the client-side twin of the admin app's
 * `useBatchSelection`, and deliberately mirrors it.
 *
 * What is NOT here: anything about how a decision is drawn. No JSX, no labels,
 * no currency formatting — the cards own all of that.
 */

/** One product's answer, in the shape the accept flow and the PDF both take. */
export interface ClientSelectionLine {
	offerId: string;
	pricingId: string;
	quantity: number;
	deliveryId?: string;
	sampleChoice: OrderSampleChoice;
}

interface UseClientBatchSelectionArgs {
	batch: Batch;
	itemLookup: Map<string, ClientQuoteItem>;
	/**
	 * Reports the combination upward, live. The accept flow no longer asks for
	 * products, quantities or delivery — it accepts what the card shows — so it
	 * needs the real state, not the debounced copy that is persisted.
	 */
	onSelectionChange?: (batchId: string, state: { selections: ClientSelectionLine[] }) => void;
}

export function useClientBatchSelection({
	batch,
	itemLookup,
	onSelectionChange,
}: UseClientBatchSelectionArgs) {
	const isPending = batch.status === OrderQuoteOfferStatus.Pending;
	const isAccepted = batch.status === OrderQuoteOfferStatus.Accepted;
	const selectedCurrency = useQuoteCurrency();

	// The user's persisted combination, if any — the 'master' (downloaded-quote)
	// selection when it exists, else their last working (debounced) one. Preferred
	// over the default on load; a pricing/delivery id that no longer exists (the
	// admin edited the offer since) falls back to the default below.
	const restoreFrom = batch.savedSelection;
	const savedByOffer = new Map((restoreFrom?.selections ?? []).map((s) => [s.offerId, s]));

	const resolvePricingId = (offer: Offer): string | undefined => {
		// Once accepted there is nothing left to preview: the card states the
		// combination the client agreed to, and a working selection saved before
		// they accepted must never overrule it.
		if (isAccepted) return offer.acceptedPricingId ?? offer.pricing?.[0]?.id;
		const savedId = savedByOffer.get(offer.id)?.pricingId;
		if (savedId && offer.pricing?.some((p) => p.id === savedId)) return savedId;
		return offer.acceptedPricingId ?? offer.pricing?.[0]?.id;
	};

	const initialPricings = () => {
		const initial: Record<string, string> = {};
		for (const offer of batch.offers) {
			const pricingId = resolvePricingId(offer);
			if (pricingId) initial[offer.id] = pricingId;
		}
		return initial;
	};

	// Preview selection (one tier + delivery per offer) — drives the live summary
	// below. Non-binding, mirrors admin's card; unrelated to accepting offers.
	const [selectedPricings, setSelectedPricings] =
		useState<Record<string, string>>(initialPricings);
	const [selectedDeliveries, setSelectedDeliveries] = useState<Record<string, string>>(() => {
		const pricings = initialPricings();
		const initial: Record<string, string> = {};
		for (const offer of batch.offers) {
			const tier = offer.pricing?.find((p) => p.id === pricings[offer.id]);
			// Both a saved and an accepted delivery are only valid if they belong to
			// the resolved tier, which may have moved since either was recorded.
			const savedDeliveryId = savedByOffer.get(offer.id)?.deliveryId;
			const belongs = (id?: string | null) =>
				!!id && !!tier?.deliveries?.some((d) => d.id === id);

			// Accepted: the client's own answer, and nothing else. A null
			// `acceptedDeliveryId` means nothing was picked, so falling through to
			// the tier's first option would show them a method they never chose,
			// priced into the total.
			if (isAccepted) {
				if (belongs(offer.acceptedDeliveryId)) {
					initial[offer.id] = offer.acceptedDeliveryId as string;
				}
				continue;
			}

			const deliveryId =
				(belongs(savedDeliveryId) ? savedDeliveryId : undefined) ??
				(belongs(offer.acceptedDeliveryId) ? offer.acceptedDeliveryId : undefined) ??
				tier?.deliveries?.[0]?.id;
			if (deliveryId) initial[offer.id] = deliveryId;
		}
		return initial;
	});

	// Products the client has taken out of this offer. Everything starts included,
	// deliberately: `buildSelections` already omits offers for reasons that aren't
	// exclusion (no priced tier, no known quantity), so an offer missing from the
	// saved selection doesn't uniquely mean "left out" — inferring it would
	// silently deselect products the client never touched.
	const [excludedOfferIds, setExcludedOfferIds] = useState<Set<string>>(new Set<string>());
	const includedOffers = batch.offers.filter((o) => !excludedOfferIds.has(o.id));

	// The sample decision per product — the third thing this card chooses, after
	// the quantity tier and the shipping method. Only an offer that HAS a sample
	// gets an entry; everything else is NO_SAMPLE and stays that way.
	//
	// Restored from the saved selection, but only when it's still answerable: a
	// choice that needs a sample is meaningless once the admin has taken the
	// sample off the offer, exactly as the accept flow guards it.
	const [sampleChoices, setSampleChoices] = useState<Record<string, OrderSampleChoice>>(() => {
		const initial: Record<string, OrderSampleChoice> = {};
		for (const offer of batch.offers) {
			if (!offer.sample) continue;
			initial[offer.id] =
				savedByOffer.get(offer.id)?.sampleChoice ?? OrderSampleChoice.NoSample;
		}
		return initial;
	});

	// Show the "restored from your downloaded quote" hint only until the user
	// touches the combination — after that it no longer reflects what's on screen.
	const [hasInteracted, setHasInteracted] = useState(false);
	const showRestoredHint =
		!isAccepted && !!batch.savedSelection?.fromDownloadedQuote && !hasInteracted;

	const [fetchSummary, { data: liveData, previousData: livePrevious }] =
		useGetClientOrderQuoteOfferBatchSummaryLazyQuery();

	// `batch.summary` is the batch's DEFAULTS — first tier, first delivery, no
	// plan — so it is never what the card is showing. Falling back to it printed a
	// total for a combination nobody had chosen. Only a live summary is a price.
	//
	// `previousData` covers the gap while a refetch is in flight: with no-cache
	// Apollo clears `data` on every variable change, which would otherwise blank
	// the total on each tier or method click.
	const liveSummary =
		(liveData ?? livePrevious)?.getClientOrderQuoteOfferBatches?.find((b) => b.id === batch.id)
			?.summary ?? null;

	// Once a live summary is shown, the lazy `liveData` always wins over
	// `batch.summary`. If the offer's content changes (the parent refetches and
	// `batch.summary` updates), the lazy summary would stay stale — re-fetch it
	// for the current selection whenever the batch summary or currency changes.
	const summarySignature = JSON.stringify(batch.summary);

	const refetchSummary = (
		pricings: Record<string, string>,
		deliveries: Record<string, string>,
		excluded: Set<string> = excludedOfferIds,
		choices: Record<string, OrderSampleChoice> = sampleChoices
	) => {
		const included = batch.offers.filter((o) => !excluded.has(o.id));
		const includedOfferIds = included.map((o) => o.id);
		const selectedPricingIds = included.map((o) => pricings[o.id]).filter(Boolean);
		const selectedDeliveryIds = included.map((o) => deliveries[o.id]).filter(Boolean);

		// Taking everything out is a real state that totals zero — only bail when
		// there are products in but no tier picked for any of them yet.
		if (includedOfferIds.length > 0 && selectedPricingIds.length === 0) return;

		fetchSummary({
			variables: {
				orderQuoteId: batch.orderQuoteId,
				inCurrency: selectedCurrency,
				selectedPricingIds,
				selectedDeliveryIds:
					selectedDeliveryIds.length > 0 ? selectedDeliveryIds : undefined,
				// Always sent, never omitted: the API reads an omitted list as
				// "total everything" and an empty one as "total nothing", so
				// dropping it when everything is excluded would total the batch.
				includedOfferIds,
				// Same rule, same reason: omitting the list counts every authored
				// sample as taken with shipping. An offer absent from it is
				// NO_SAMPLE, which is exactly how acceptance reads a missing choice —
				// so offers without a sample simply don't appear.
				selectedSampleChoices: included
					.filter((o) => o.sample)
					.map((o) => ({
						offerId: o.id,
						sampleChoice: choices[o.id] ?? OrderSampleChoice.NoSample,
					})),
			},
			fetchPolicy: 'no-cache',
		});
	};

	useEffect(() => {
		refetchSummary(selectedPricings, selectedDeliveries);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [summarySignature, selectedCurrency]);

	const selectPricing = (offerId: string, pricingId: string) => {
		// Already the selected tier — don't re-fetch or reset the chosen delivery.
		if (selectedPricings[offerId] === pricingId) return;
		setHasInteracted(true);

		const nextPricings = { ...selectedPricings, [offerId]: pricingId };
		setSelectedPricings(nextPricings);
		// Reset delivery to the new tier's first option.
		const tier = batch.offers
			.find((o) => o.id === offerId)
			?.pricing?.find((p) => p.id === pricingId);
		const nextDeliveries = { ...selectedDeliveries };
		const deliveryId = tier?.deliveries?.[0]?.id;
		if (deliveryId) nextDeliveries[offerId] = deliveryId;
		else delete nextDeliveries[offerId];
		setSelectedDeliveries(nextDeliveries);
		refetchSummary(nextPricings, nextDeliveries);
	};

	const selectDelivery = (offerId: string, deliveryId: string, pricingId: string) => {
		// A delivery belongs to a specific tier — picking one from a tier that
		// isn't the selected one switches the tier too, so the previewed delivery
		// always sits on the chosen pricing.
		const tierChanged = selectedPricings[offerId] !== pricingId;
		if (!tierChanged && selectedDeliveries[offerId] === deliveryId) return;
		setHasInteracted(true);

		const nextPricings = tierChanged
			? { ...selectedPricings, [offerId]: pricingId }
			: selectedPricings;
		const nextDeliveries = { ...selectedDeliveries, [offerId]: deliveryId };

		if (tierChanged) setSelectedPricings(nextPricings);
		setSelectedDeliveries(nextDeliveries);
		refetchSummary(nextPricings, nextDeliveries);
	};

	// Re-prices like any other part of the combination: the summary answers to
	// `selectedSampleChoices`, so opting down to video/images or out entirely moves
	// the Samples line and the total with it.
	const selectSampleChoice = (offerId: string, choice: OrderSampleChoice) => {
		if (sampleChoices[offerId] === choice) return;
		setHasInteracted(true);
		const next = { ...sampleChoices, [offerId]: choice };
		setSampleChoices(next);
		refetchSummary(selectedPricings, selectedDeliveries, excludedOfferIds, next);
	};

	// Taking a product out leaves it in the offer, priced and visible — it just
	// stops counting toward the order. Putting it back restores the tier and
	// method it already had.
	//
	// Every product is the client's to take out. A taken plan used to own the ones
	// it pinned — its price covered them as a set — and that was the only thing
	// that ever refused this.
	const toggleOffer = (offerId: string) => {
		setHasInteracted(true);
		const next = new Set(excludedOfferIds);
		if (next.has(offerId)) next.delete(offerId);
		else next.add(offerId);
		setExcludedOfferIds(next);
		refetchSummary(selectedPricings, selectedDeliveries, next);
	};

	// Build the accept-style selection payload for the current combination —
	// shared by the PDF (the server marks it the 'master' selection) and the
	// debounced persist below. Skips any offer without a priced/known quantity.
	const buildSelections = (): ClientSelectionLine[] =>
		includedOffers
			.map((offer) => {
				const pricingId = selectedPricings[offer.id];
				if (!pricingId) return null;
				const pricing = offer.pricing?.find((p) => p.id === pricingId);
				const quantity = itemLookup
					.get(offer.orderQuoteItemId)
					?.quantities.find((q) => q.id === pricing?.orderQuoteItemQuantityId)?.quantity;
				if (!quantity) return null;
				return {
					offerId: offer.id,
					pricingId,
					quantity,
					deliveryId: selectedDeliveries[offer.id] || undefined,
					sampleChoice: sampleChoices[offer.id] ?? OrderSampleChoice.NoSample,
				};
			})
			.filter((s): s is NonNullable<typeof s> => s !== null);

	// Report the combination on every change, including the initial one — a
	// restored or default tier is as much a choice as a clicked one.
	useEffect(() => {
		onSelectionChange?.(batch.id, { selections: buildSelections() });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPricings, selectedDeliveries, excludedOfferIds, sampleChoices]);

	// Persist the working selection (debounced) so it survives leaving/returning
	// even without downloading a PDF. Best-effort — never surface errors. A
	// downloaded PDF sets the 'master' selection server-side, which always wins on
	// reload, so these working saves never override it. Skips the initial mount
	// (nothing changed yet — avoids a redundant write of the restored/default).
	const [saveSelection] = useSaveOfferBatchSelectionMutation();
	const didMountRef = useRef(false);
	useEffect(() => {
		if (!didMountRef.current) {
			didMountRef.current = true;
			return;
		}
		// An accepted batch has no working selection to keep — what counts is on
		// the order now, and writing over it would only muddy the record.
		if (isAccepted) return;
		const selections = buildSelections();
		if (selections.length === 0) return;
		const timer = setTimeout(() => {
			saveSelection({
				variables: { input: { batchId: batch.id, selections } },
			}).catch(() => {});
		}, 600);
		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPricings, selectedDeliveries, excludedOfferIds, sampleChoices]);

	// Draft e-conomic quote PDF for the currently selected combination — never
	// emailed. Always issued in DKK (the accounting base) regardless of the
	// on-screen display currency. Deduped by combination server-side; the URL is
	// short-lived, so it opens straight into the in-app preview (download
	// available from there) and is never persisted.
	const [generatePdf, { loading: pdfLoading }] = useGenerateOfferEconomicQuotePdfMutation();
	const [pdfPreview, setPdfPreview] = useState<FilePreviewItem | null>(null);

	const downloadPdf = async () => {
		const selections = buildSelections();

		if (selections.length === 0) {
			toast.error('Select a quantity tier for each product first.');
			return;
		}

		try {
			const { data } = await generatePdf({
				variables: {
					input: {
						orderQuoteId: batch.orderQuoteId,
						batchId: batch.id,
						selections,
					},
				},
			});
			const result = data?.generateOfferEconomicQuotePdf;
			if (result?.success && result.pdfUrl) {
				setPdfPreview({
					id: `quote-${result.economicQuoteNumber ?? batch.id}`,
					url: result.pdfUrl,
					name: result.economicQuoteNumber
						? `Quote ${result.economicQuoteNumber}.pdf`
						: 'Quote.pdf',
					mimeType: 'application/pdf',
				});
			} else {
				toast.error(result?.message || 'Could not generate the quote PDF.');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not generate the quote PDF.');
		}
	};

	return {
		/* ── where the batch stands ── */
		isPending,
		isAccepted,

		/* ── the five answers ── */
		selectedPricings,
		selectedDeliveries,
		excludedOfferIds,
		sampleChoices,

		/* ── what follows from them ── */
		includedOffers,
		liveSummary,
		showRestoredHint,

		/* ── changing them ── */
		selectPricing,
		selectDelivery,
		selectSampleChoice,
		toggleOffer,
		buildSelections,

		/* ── the quote PDF for the combination on screen ── */
		pdfLoading,
		pdfPreview,
		clearPdfPreview: () => setPdfPreview(null),
		downloadPdf,
	};
}
